import React, { useState } from 'react';
import { Modal, Upload, Button, Progress, Table, Tag, Space, App } from 'antd';
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import { importLedgerRecord } from '@/services/ant-design-pro/api';
import { downloadFile, buildDownloadUrl } from '@/utils/download';

const { Dragger } = Upload;

interface LedgerImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const LedgerImportModal: React.FC<LedgerImportModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<{
    numberFail: number;
    numberSuccesses: number;
    totalCount: number;
    succeedList: any[];
    failList: any[];
    storageVO?: { id?: string | number };
  } | null>(null);
  const [step, setStep] = useState<'upload' | 'result'>('upload');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [failPagination, setFailPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // 重置状态
  const resetState = () => {
    setFileList([]);
    setImportResult(null);
    setStep('upload');
    setPagination({ current: 1, pageSize: 10 });
    setFailPagination({ current: 1, pageSize: 10 });
  };

  // 处理取消
  const handleCancel = () => {
    resetState();
    onCancel();
  };

  // 处理文件上传
  const handleUpload = {
    beforeUpload: (file: File) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('只能上传Excel文件!');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB!');
        return false;
      }
      setFileList([file]);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  // 处理导入
  const handleImport = async () => {
    try {
      if (fileList.length === 0) {
        message.error('请选择要导入的文件!');
        return;
      }

      setLoading(true);
      const response = await importLedgerRecord(fileList[0]);

      if (response.success && response.result) {
        setImportResult({
          numberFail: response.result.numberFail || 0,
          numberSuccesses: response.result.numberSuccesses || 0,
          totalCount: response.result.totalCount || 0,
          succeedList: response.result.succeedList || [],
          failList: response.result.failList || [],
          storageVO: response.result.storageVO,
        });
        setStep('result');

        const { numberSuccesses, numberFail } = response.result;
        if (numberSuccesses > 0 && numberFail === 0) {
          message.success(`导入完成! 成功导入 ${numberSuccesses} 条台账记录`);
        } else if (numberSuccesses > 0 && numberFail > 0) {
          message.warning(`导入完成! 成功导入 ${numberSuccesses} 条，失败 ${numberFail} 条`);
        } else {
          message.error(`导入失败! 共 ${numberFail} 条记录导入失败`);
        }
      } else {
        message.error(response.message || '导入失败');
      }
    } catch (error) {
      console.error('导入台账记录失败:', error);
      message.error('导入失败');
    } finally {
      setLoading(false);
    }
  };

  // 下载失败文件
  const handleDownloadFailed = () => {
    if (!importResult?.storageVO?.id) {
      message.error('失败文件不存在');
      return;
    }

    try {
      // 构建下载URL
      const downloadUrl = buildDownloadUrl(importResult.storageVO.id);

      // 使用通用下载方法
      downloadFile(downloadUrl, '导入失败台账记录列表.xlsx');

      message.success('失败文件下载成功');
    } catch (error) {
      console.error('下载失败文件失败:', error);
      message.error('下载失败文件失败，请稍后重试');
    }
  };

  // 结果表格列定义
  const resultColumns = [
    {
      title: '报告编号',
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      width: 150,
      ellipsis: true,
    },
    {
      title: '任务名称',
      dataIndex: 'taskName',
      key: 'taskName',
      width: 200,
      ellipsis: true,
    },
    {
      title: '业务类型',
      dataIndex: 'businessType',
      key: 'businessType',
      width: 120,
    },
    {
      title: '业务子类型',
      dataIndex: 'businessSubTypeName',
      key: 'businessSubTypeName',
      width: 160,
      ellipsis: true,
    },
    {
      title: '业务领域',
      dataIndex: 'businessLines',
      key: 'businessLines',
      width: 120,
    },
    {
      title: '任务编号',
      dataIndex: 'taskNumber',
      key: 'taskNumber',
      width: 150,
      ellipsis: true,
    },
    {
      title: '所在省份',
      dataIndex: 'locationProvince',
      key: 'locationProvince',
      width: 120,
    },
    {
      title: '所属城市',
      dataIndex: 'taskCity',
      key: 'taskCity',
      width: 120,
    },
    {
      title: '所属部门',
      dataIndex: 'departmentName',
      key: 'departmentName',
      width: 140,
      ellipsis: true,
      render: (text: string) => text || '-',
    },
  ];

  return (
    <Modal
      title="导入台账记录"
      open={visible}
      onCancel={handleCancel}
      width={1000}
      maskClosable={false}
      footer={
        step === 'upload' ? (
          <Space>
            <Button onClick={handleCancel}>取消</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleImport}
              disabled={fileList.length === 0}
            >
              开始导入
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={handleCancel}>关闭</Button>
            {importResult?.storageVO && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadFailed}
              >
                下载失败文件
              </Button>
            )}
            <Button type="primary" onClick={() => {
              resetState();
              onSuccess();
            }}>
              完成
            </Button>
          </Space>
        )
      }
    >
      {step === 'upload' ? (
        <div>
          <div style={{ marginTop: 16 }}>
            <Dragger
              {...handleUpload}
              fileList={fileList}
              accept=".xlsx,.xls"
              maxCount={1}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持单个Excel文件上传，文件大小不超过10MB
              </p>
            </Dragger>
          </div>

          {loading && (
            <div style={{ marginTop: 16 }}>
              <Progress percent={50} status="active" />
              <p style={{ textAlign: 'center', marginTop: 8 }}>
                正在导入台账记录，请稍候...
              </p>
            </div>
          )}

          {!loading && (
            <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
              <h4>导入说明：</h4>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>支持填写新版模板，或编辑导出台账文件后重新导入，请保留原表头</li>
                <li>报告编号必填：已有编号更新原台账，新编号新增且须填写所属机构</li>
                <li>空白单元格和未导出的列保留原值，0 和“否”会正常更新</li>
                <li>任务ID须与原记录一致；创建人、系统创建时间、首次签章时间、特殊情况和直连信息由系统维护</li>
                <li>导入过程中如有错误，系统会生成失败文件供下载查看</li>
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 16 }}>
            <h4>导入结果</h4>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
              <Tag color="success">
                成功: {importResult?.numberSuccesses || 0} 条
              </Tag>
              <Tag color="error">
                失败: {importResult?.numberFail || 0} 条
              </Tag>
              <Tag color="processing">
                总计: {(importResult?.numberSuccesses || 0) + (importResult?.numberFail || 0)} 条
              </Tag>
            </div>
          </div>

          {importResult && importResult.succeedList && importResult.succeedList.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4>成功导入的记录</h4>
              <Table
                columns={resultColumns}
                dataSource={importResult.succeedList}
                rowKey={(record, index) => `success-${record.reportNumber || record.taskName || 'record'}-${index}`}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: importResult.succeedList.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 条记录`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  onChange: (page, pageSize) => {
                    setPagination({
                      current: page,
                      pageSize: pageSize || pagination.pageSize,
                    });
                  },
                  onShowSizeChange: (current, size) => {
                    setPagination({
                      current: 1,
                      pageSize: size,
                    });
                  },
                }}
                scroll={{ x: 1000 }}
                size="small"
              />
            </div>
          )}

          {importResult && importResult.failList && importResult.failList.length > 0 && (
            <div>
              <h4>导入失败的记录</h4>
              <Table
                columns={resultColumns}
                dataSource={importResult.failList}
                rowKey={(record, index) => `fail-${record.reportNumber || record.taskName || 'record'}-${index}`}
                pagination={{
                  current: failPagination.current,
                  pageSize: failPagination.pageSize,
                  total: importResult.failList.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 条记录`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  onChange: (page, pageSize) => {
                    setFailPagination({
                      current: page,
                      pageSize: pageSize || failPagination.pageSize,
                    });
                  },
                  onShowSizeChange: (current, size) => {
                    setFailPagination({
                      current: 1,
                      pageSize: size,
                    });
                  },
                }}
                scroll={{ x: 1000 }}
                size="small"
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default LedgerImportModal;

